import { blobToFile } from './blob';
import { readAsImgAsync } from './files';

export type ImageOutputMimeType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface ImageResizeOptions {
    /**Output format. Defaults to the supported source format, then PNG.*/
    mimeType?: ImageOutputMimeType;
    /**Encoder quality from 0 to 1. Browsers ignore it for formats such as PNG.*/
    quality?: number;
}

interface ImageSize {
    readonly width: number;
    readonly height: number;
}

interface ImageEncoding {
    readonly mimeType: ImageOutputMimeType;
    readonly quality?: number;
}

/**Downsizes an image to fit maxSize while preserving its ratio. Never enlarges it.*/
export async function resizeByMaxSize(
    file: File,
    maxSize: number,
    options?: ImageResizeOptions
): Promise<File> {
    const targetMaxSize = normalizeDimension(maxSize, 'maxSize');
    const encoding = resolveEncoding(file, options);
    const image = await readAsImgAsync(file);
    const source = getImageSize(image);

    if (source.width <= targetMaxSize && source.height <= targetMaxSize) return file;

    const scale = targetMaxSize / Math.max(source.width, source.height);
    const target = {
        width: Math.max(1, Math.round(source.width * scale)),
        height: Math.max(1, Math.round(source.height * scale))
    };
    return renderToFile(file, image, target, 0, 0, target.width, target.height, encoding);
}

/**Resizes an image to an exact square and crops overflow from the center. May enlarge it.*/
export async function resizeToCuttedSquare(
    file: File,
    sideSize: number,
    options?: ImageResizeOptions
): Promise<File> {
    return resizeToCuttedRect(file, sideSize, sideSize, options);
}

/**Resizes an image to an exact rectangle and crops overflow from the center. May enlarge it.*/
export async function resizeToCuttedRect(
    file: File,
    width: number,
    height: number,
    options?: ImageResizeOptions
): Promise<File> {
    const target = {
        width: normalizeDimension(width, 'width'),
        height: normalizeDimension(height, 'height')
    };
    const encoding = resolveEncoding(file, options);
    const image = await readAsImgAsync(file);
    const source = getImageSize(image);

    const scale = Math.max(target.width / source.width, target.height / source.height);
    const imageWidth = source.width * scale;
    const imageHeight = source.height * scale;
    const imageX = (target.width - imageWidth) / 2;
    const imageY = (target.height - imageHeight) / 2;
    return renderToFile(file, image, target, imageX, imageY, imageWidth, imageHeight, encoding);
}

async function renderToFile(
    sourceFile: File,
    image: HTMLImageElement,
    canvasSize: ImageSize,
    imageX: number,
    imageY: number,
    imageWidth: number,
    imageHeight: number,
    encoding: ImageEncoding
): Promise<File> {
    const blob = await fillCanvas(
        image,
        canvasSize,
        imageX,
        imageY,
        imageWidth,
        imageHeight,
        encoding
    );
    const actualMimeType = normalizeMimeType(blob.type);
    if (!actualMimeType)
        throw new Error(`Canvas returned unsupported image MIME type '${blob.type || 'empty'}'.`);
    return blobToFile(blob, getOutputFileName(sourceFile.name, actualMimeType));
}

function fillCanvas(
    image: HTMLImageElement,
    canvasSize: ImageSize,
    imageX: number,
    imageY: number,
    imageWidth: number,
    imageHeight: number,
    encoding: ImageEncoding
): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('A 2D canvas context is unavailable.');
    context.drawImage(image, imageX, imageY, imageWidth, imageHeight);

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(blob => {
            if (blob) resolve(blob);
            else reject(new Error(`Canvas could not encode '${encoding.mimeType}'.`));
        }, encoding.mimeType, encoding.quality);
    });
}

function getImageSize(image: HTMLImageElement): ImageSize {
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0)
        throw new Error('Decoded image dimensions must be finite positive numbers.');
    return { width, height };
}

function normalizeDimension(value: number, name: string): number {
    if (!Number.isFinite(value) || value <= 0)
        throw new RangeError(`${name} must be a finite positive number.`);
    return Math.max(1, Math.round(value));
}

function resolveEncoding(file: File, options?: ImageResizeOptions): ImageEncoding {
    const requestedMimeType = options?.mimeType;
    const mimeType = requestedMimeType !== undefined
        ? normalizeMimeType(requestedMimeType)
        : normalizeMimeType(file.type) ?? inferMimeType(file.name) ?? 'image/png';
    if (!mimeType)
        throw new RangeError(`Unsupported output image MIME type '${requestedMimeType}'.`);

    const quality = options?.quality;
    if (quality !== undefined && (!Number.isFinite(quality) || quality < 0 || quality > 1))
        throw new RangeError('quality must be a finite number from 0 to 1.');
    return quality === undefined ? { mimeType } : { mimeType, quality };
}

function normalizeMimeType(value: string): ImageOutputMimeType | undefined {
    switch (value.trim().toLowerCase()) {
        case 'image/jpeg':
        case 'image/jpg':
            return 'image/jpeg';
        case 'image/png':
            return 'image/png';
        case 'image/webp':
            return 'image/webp';
        default:
            return undefined;
    }
}

function inferMimeType(fileName: string): ImageOutputMimeType | undefined {
    const extension = getFileExtension(fileName);
    if (extension === 'jpg' || extension === 'jpeg' || extension === 'jpe' || extension === 'jfif')
        return 'image/jpeg';
    if (extension === 'png') return 'image/png';
    if (extension === 'webp') return 'image/webp';
    return undefined;
}

function getOutputFileName(fileName: string, mimeType: ImageOutputMimeType): string {
    const extension = getFileExtension(fileName);
    const matchingExtensions = mimeType === 'image/jpeg'
        ? ['jpg', 'jpeg', 'jpe', 'jfif']
        : [mimeType.slice('image/'.length)];
    if (matchingExtensions.includes(extension)) return fileName;

    const dotIndex = fileName.lastIndexOf('.');
    const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName;
    const outputExtension = mimeType === 'image/jpeg' ? 'jpg' : mimeType.slice('image/'.length);
    return `${baseName || 'image'}.${outputExtension}`;
}

function getFileExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex > 0 ? fileName.slice(dotIndex + 1).toLowerCase() : '';
}
